import { reportError } from "@cabocheck/shared";
import IframeResizer from "@iframe-resizer/react";
import type { LoaderFunctionArgs } from "react-router";
import { data, useLoaderData, useParams } from "react-router";
import invariant from "invariant";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { client } from "~/client/client";
import { Button } from "~/components/ui/button";
import { PublicCalendar } from "~/features/calendar/public/PublicCalendar";
import { PublicPocProductDetail } from "~/features/poc/PublicPocProductDetail";
import { getBaseParams } from "~/utils/getBaseParams";
import { getPartnerContractTypeSet, hasNonOrdersOnlyPartnerContractType } from "~/utils/partnerProductTypes";

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { slug, productGuid } = params;

  invariant(slug, "slug is required");
  if (!productGuid) throw new Response("Not found", { status: 404 });

  const baseParams = await getBaseParams(request, { withDeviceGuid: false });

  const { data: responseData, error } = await client.v1.getApiPartnerLandingByCode({
    ...baseParams,
    code: slug,
  });

  if (error || !responseData.response) {
    reportError("Product request failed", error);
    throw new Response("Something went wrong", { status: 500 });
  }

  const activeProductTypes = getPartnerContractTypeSet(responseData.response.active_contracts);
  if (!hasNonOrdersOnlyPartnerContractType(activeProductTypes, "CaboPOC"))
    throw new Response("Not found", { status: 404 });

  const appointmentTypesResponse = await client.v1.getApiPartnerAppointmentTypeList(
    responseData.response.partner_guid,
    {
      ...baseParams,
    },
  );

  const summary = responseData.response.poc_products?.find(
    (item) => item.product_guid === productGuid && item.selling_price != null,
  );

  if (!summary) throw new Response("Not found", { status: 404 });

  const detail = await client.v1.getApiPartnerLandingPocProduct(productGuid, { ...baseParams, code: slug });
  if (detail.error || !detail.data?.response) {
    throw new Response("Could not load product", { status: detail.error?.error === "not_found" ? 404 : 500 });
  }
  const product = detail.data.response;

  return data({
    product,
    partnerLanding: responseData.response,
    appointmentTypes: appointmentTypesResponse.data?.response?.partner_appointment_types,
  });
};

export default function PartnerPocProductPage() {
  const { product, partnerLanding, appointmentTypes } = useLoaderData<typeof loader>();
  const { slug } = useParams();
  const { t } = useTranslation();

  return (
    <PublicPocProductDetail
      product={product}
      backFallbackTo={slug ? `/p/${slug}` : "/tests"}
      cta={
        partnerLanding?.iframe ? (
          partnerLanding.show_iframe_as_url ? (
            <a href={partnerLanding.iframe} target="_blank" rel="noreferrer" className="w-full sm:w-auto">
              <Button
                endIcon={<ArrowRight />}
                size="lg"
                className="w-full max-w-xs whitespace-normal break-words text-center leading-snug sm:w-auto sm:max-w-none"
              >
                {t("Hier klicken um zum Terminbuchungssystem zu gelangen")}
              </Button>
            </a>
          ) : (
            <IframeResizer
              license="GPLv3"
              log="collapsed"
              src={partnerLanding.iframe}
              style={{ width: "100%", height: "100vh" }}
            />
          )
        ) : appointmentTypes?.length ? (
          <PublicCalendar partnerLanding={partnerLanding} initialAppointmentTypes={appointmentTypes} />
        ) : null
      }
    />
  );
}
